import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { defer, of, Subject } from 'rxjs';
import { catchError, finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { HenviserDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/henviser-dto';
import { HenviserService } from 'src/app/api/services';
import { ConfirmDialogComponent } from 'src/app/components/shared/confirm-dialog/confirm-dialog.component';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { HenviserDialogComponent } from '../../henviser/henviser-dialog/henviser-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { GetHenviserRequest } from 'src/app/api/models/OK/Egis/Service/Controllers/get-henviser-request';
import { HistoryViewComponent } from '../../../shared/history/history-view/history-view.component';
import { AuditHistoryEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/audit-history-enum';
import { IHistoryView } from '../../../shared/history/history.model';
import { StamdataStatusService } from '../../shared/stamdata-status.service';
import { StamdataStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/stamdata-status-enum';
import { NotificationService } from '../../../../notifications/services/notification.service';
import { NotificationType } from '../../../../notifications/models/notification-models';

@Component({
  selector: 'app-henvisere-list',
  templateUrl: './henvisere-list.component.html',
  styleUrls: ['./henvisere-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HenvisereListComponent implements OnInit {
  henvisere: HenviserDto[] = [];
  unsubscribe$ = new Subject<void>();
  searchQuery: string;
  form: UntypedFormGroup;
  editForm: UntypedFormGroup;

  displayedColumns: string[] = ['name', 'cvr', 'henviserstatus', 'actions'];

  constructor(
    private henviserService: HenviserService,
    private errorHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private notification: NotificationService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private auth: AuthService,
    private router: Router,
    private statusservice: StamdataStatusService,
  ) {}

  get canEdit() {
    return this.auth.isAdministrator;
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        flatMap((queryParams: Params) => {
          this.searchQuery = '';
          const q = queryParams['q'];

          if (q) {
            this.searchQuery = q;
          }

          return this.getHenvisere$;
        }),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.form = this.formBuilder.group({
      cvr: ['', [Validators.required, Validators.minLength(1)]],
      name: ['', [Validators.required, Validators.minLength(3)]],
    });

    this.editForm = this.formBuilder.group({
      cvr: ['', [Validators.required, Validators.minLength(1)]],
      name: ['', [Validators.required, Validators.minLength(3)]],
    });
  }

  getHenvisere$ = defer(() => {
    this.loader.show();
    const request: GetHenviserRequest = {
      searchQuery: this.searchQuery,
    };

    return this.henviserService.getHenvisere({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      tap((response) => {
        this.henvisere = response;
      }),
      finalize(() => {
        this.loader.hide();
      }),
      takeUntil(this.unsubscribe$),
      catchError((err) => {
        this.errorHandler.HandleError(err);
        return of(null);
      }),
    );
  });

  add() {
    let ref = this.dialog.open(HenviserDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { searchQuery: this.searchQuery },
    });

    ref
      .afterClosed()
      .pipe(
        flatMap(() => {
          this.reset();
          return this.getHenvisere$;
        }),
      )
      .subscribe((p) => (this.historyView = null));
  }

  edit(henviser: HenviserDto) {
    let dialogRef = this.dialog.open(HenviserDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { henviser: henviser, id: henviser.id, cvr: henviser.cvr, name: henviser.name },
    });

    dialogRef.afterClosed().subscribe((p) => {
      if (p) {
        const reload = this.historyView && henviser && henviser.name != p.name;
        Object.assign(henviser, p);
        if (reload) this.showhistory(henviser);
        this.historyViewComponent.render();
      }
    });
  }

  remove(henviser: HenviserDto) {
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Slet henviser?',
        body: 'Er du sikker på at du vil fjerne ' + henviser.name + '?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        flatMap((confirm) => {
          if (!confirm) return of(null);
          return this.henviserService
            .removeHenviser({
              caller: EGIS_SERVICE_CALLER,
              id: henviser.id,
            })
            .pipe(
              flatMap(() => {
                return this.getHenvisere$;
              }),
            );
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.historyView = null;
            this.notification.success(`Henviser med cvr: ${henviser.cvr} er slettet`);
          }
        },
        error: (err) => this.errorHandler.HandleError(err),
      });
  }

  reset() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  @ViewChild(HistoryViewComponent) historyViewComponent: HistoryViewComponent;
  historyView: IHistoryView;
  showhistory(row: HenviserDto) {
    this.historyView = { entityId: row.id, historyType: AuditHistoryEnum.Henviser, title: row.name };
  }

  henviserstatus(henviserstatus: StamdataStatusEnum) {
    return this.statusservice.stamdatastatus(henviserstatus);
  }
}
